import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Archive, ArchiveRestore, Pencil, Pin, PinOff, Tags, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { RowActionItem } from '@/components/ui/row-actions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateFolder, useDeleteFolder, useUpdateFolder } from '../hooks/folders';
import type { Folder, FolderPurpose } from '../api/types';
import { cn } from '@/lib/utils';
import { FOLDER_PURPOSES, FOLDER_PURPOSE_LABELS, folderPurpose, folderStatus, totalLeadCount } from './folderModel';
import { Note } from './ui';

/** Clipping / Vyral / Influencer, as one segmented choice. */
export function PurposePicker({
  value,
  onChange,
  disabled,
  labelledBy,
}: {
  value: FolderPurpose;
  onChange: (purpose: FolderPurpose) => void;
  disabled?: boolean;
  labelledBy: string;
}): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className="inline-flex w-fit flex-wrap rounded-lg border border-input bg-[var(--input-bg)] p-0.5"
    >
      {FOLDER_PURPOSES.map((p) => (
        <button
          key={p}
          type="button"
          role="radio"
          aria-checked={value === p}
          disabled={disabled}
          onClick={() => onChange(p)}
          className={cn(
            'h-8 rounded-md px-3 text-xs font-semibold transition-colors pointer-coarse:h-10 disabled:opacity-60',
            value === p ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {FOLDER_PURPOSE_LABELS[p]}
        </button>
      ))}
    </div>
  );
}

export function FolderPurposeDialog({
  folder,
  open,
  onOpenChange,
}: {
  folder: Folder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const update = useUpdateFolder();
  const current = folderPurpose(folder);
  const [purpose, setPurpose] = useState<FolderPurpose>(current);
  useEffect(() => {
    if (open) {
      setPurpose(current);
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current]);

  function submit(ev: FormEvent) {
    ev.preventDefault();
    if (purpose === current) return onOpenChange(false);
    update.mutate(
      { publicId: folder.publicId, patch: { purpose } },
      {
        onSuccess: (saved) => {
          onOpenChange(false);
          const label = FOLDER_PURPOSE_LABELS[purpose];
          if (saved.purposeSynced === false) {
            toast.warning(`"${folder.name}" is now for ${label}`, {
              description: "The Lead queue couldn't be told yet. It will catch up the next time this folder is sent to outreach.",
            });
          } else {
            toast.success(`"${folder.name}" is now for ${label}`);
          }
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle id="folder-purpose-title">What is this folder for?</DialogTitle>
            <DialogDescription>
              Splits the Lead queue: leads already sent from this folder move with it.
            </DialogDescription>
          </DialogHeader>
          <PurposePicker value={purpose} onChange={setPurpose} disabled={update.isPending} labelledBy="folder-purpose-title" />
          {update.error && <Note tone="error">{(update.error as Error).message}</Note>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RenameFolderDialog({
  folder,
  open,
  onOpenChange,
}: {
  folder: Folder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const update = useUpdateFolder();
  const [name, setName] = useState(folder.name);
  useEffect(() => {
    if (open) setName(folder.name);
  }, [open, folder.name]);

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const next = name.trim();
    if (!next || next === folder.name) return onOpenChange(false);
    update.mutate(
      { publicId: folder.publicId, patch: { name: next } },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
            <DialogDescription>Outreachers see this name next to the folder's leads.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="rename-folder">Name</Label>
            <Input id="rename-folder" value={name} maxLength={120} autoFocus onChange={(ev) => setName(ev.target.value)} />
          </div>
          {update.error && <Note tone="error">{(update.error as Error).message}</Note>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Deleting drops every lead in the folder, so a non-empty folder asks for its
 * name. Archive is offered as the reversible alternative.
 */
export function DeleteFolderDialog({
  folder,
  open,
  onOpenChange,
  onDeleted,
}: {
  folder: Folder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}): JSX.Element {
  const del = useDeleteFolder();
  const update = useUpdateFolder();
  const [typed, setTyped] = useState('');
  const leadCount = totalLeadCount(folder);
  useEffect(() => {
    if (open) {
      setTyped('');
      del.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canDelete = !del.isPending && (leadCount === 0 || typed.trim() === folder.name);

  function confirm() {
    if (!canDelete) return;
    del.mutate(
      { publicId: folder.publicId, confirmLeadCount: leadCount },
      {
        onSuccess: () => {
          toast.success(`Deleted "${folder.name}"`);
          onOpenChange(false);
          onDeleted?.();
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            confirm();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete "{folder.name}"?</DialogTitle>
          <DialogDescription>
            {leadCount > 0
              ? `This removes ${leadCount.toLocaleString()} lead${leadCount === 1 ? '' : 's'} from the folder for good. Archive it instead to keep them.`
              : 'This folder is empty.'}
          </DialogDescription>
        </DialogHeader>
        {leadCount > 0 && (
          <div className="grid gap-1.5">
            <Label htmlFor="delete-folder-confirm">Type the folder name to confirm</Label>
            <Input
              id="delete-folder-confirm"
              value={typed}
              placeholder={folder.name}
              autoFocus
              autoComplete="off"
              onChange={(ev) => setTyped(ev.target.value)}
            />
          </div>
        )}
        {del.error && <Note tone="error">{(del.error as Error).message}</Note>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {leadCount > 0 && folderStatus(folder) !== 'archived' && (
            <Button
              variant="outline"
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { publicId: folder.publicId, patch: { archived: true } },
                  { onSuccess: () => onOpenChange(false) },
                )
              }
            >
              Archive instead
            </Button>
          )}
          <Button variant="destructive" disabled={!canDelete} onClick={confirm}>
            {del.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NewFolderDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (folder: Folder) => void;
}): JSX.Element {
  const create = useCreateFolder();
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<FolderPurpose>('clipping');
  useEffect(() => {
    if (open) {
      setName('');
      setPurpose('clipping');
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const n = name.trim();
    if (!n) return;
    create.mutate(
      { name: n, purpose },
      {
        onSuccess: (folder) => {
          onOpenChange(false);
          onCreated(folder);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
            <DialogDescription>A folder holds the creators one search brief turns up.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="new-folder-name">Name</Label>
            <Input
              id="new-folder-name"
              value={name}
              maxLength={120}
              autoFocus
              placeholder="e.g. UK gaming editors"
              onChange={(ev) => setName(ev.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label id="new-folder-purpose">For</Label>
            <PurposePicker value={purpose} onChange={setPurpose} disabled={create.isPending} labelledBy="new-folder-purpose" />
          </div>
          {create.error && <Note tone="error">{(create.error as Error).message}</Note>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create folder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Rename / Set purpose / Pin / Archive-or-Restore / Delete for one folder, as RowActions
 * items plus the dialogs they open. The dialogs live outside the menu, since
 * a menu item unmounts the moment it's clicked.
 */
export function useFolderMenu(folder: Folder, opts: { onDeleted?: () => void } = {}) {
  const update = useUpdateFolder();
  const [renaming, setRenaming] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const status = folderStatus(folder);
  const isArchived = status === 'archived';

  const restore = () =>
    update.mutate(
      { publicId: folder.publicId, patch: { status: 'active', archived: false } },
      { onSuccess: () => toast.success(`Restored "${folder.name}"`) },
    );
  const archive = () =>
    update.mutate(
      { publicId: folder.publicId, patch: { archived: true } },
      { onSuccess: () => toast.success(`Archived "${folder.name}"`, { description: 'Its schedules are paused.' }) },
    );

  const items: Record<'rename' | 'purpose' | 'pin' | 'archive' | 'delete', RowActionItem> = {
    rename: { label: 'Rename', icon: Pencil, onClick: () => setRenaming(true) },
    purpose: { label: 'Set purpose…', icon: Tags, onClick: () => setTagging(true) },
    pin: {
      label: folder.pinnedAt ? 'Unpin' : 'Pin',
      icon: folder.pinnedAt ? PinOff : Pin,
      onClick: () => update.mutate({ publicId: folder.publicId, patch: { pinned: !folder.pinnedAt } }),
    },
    archive: isArchived
      ? { label: 'Restore', icon: ArchiveRestore, onClick: restore }
      : { label: 'Archive', icon: Archive, onClick: archive },
    delete: { label: 'Delete…', icon: Trash2, destructive: true, onClick: () => setDeleting(true) },
  };

  const dialogs = (
    <>
      <RenameFolderDialog folder={folder} open={renaming} onOpenChange={setRenaming} />
      <FolderPurposeDialog folder={folder} open={tagging} onOpenChange={setTagging} />
      <DeleteFolderDialog folder={folder} open={deleting} onOpenChange={setDeleting} onDeleted={opts.onDeleted} />
    </>
  );

  return { items, dialogs, restore, isPending: update.isPending };
}
